import { useEffect, useState } from 'react';
import { idsToRecord, loadExportHistory, recordExported, splitByHistory } from '../lib/albumExport/exportHistory';
import { buildExportItems } from '../lib/albumExport/exportItems';
import { incrementalSuffix } from '../lib/albumExport/exportNaming';
import { runExport } from '../lib/albumExport/exportRunner';
import { createFolderSink } from '../lib/albumExport/folderSink';
import { measureItems } from '../lib/albumExport/measureItems';
import { planParts } from '../lib/albumExport/planParts';
import { saveBlob } from '../lib/albumExport/saveBlob';
import { createZipSink } from '../lib/albumExport/zipSink';
import type { ExportItem, ExportProgress, ExportResult, ExportSink, ExportSource, FsDirHandle } from '../lib/albumExport/types';

export type ExportMode = 'folder' | 'zip';
/** 'new' = only items this browser has not exported before. */
export type ExportScope = 'new' | 'all';

/** What a retry needs from the run before it. */
export interface ExportContext {
  /** The folder being written to; null for a ZIP export. Holds only directory handles, so a retry reuses it. */
  folderSink: ExportSink | null;
  allItems: ExportItem[];
  zipPartsUsed: number;
  nameSuffix: string;
}

export type ExportState =
  | { status: 'idle' }
  | { status: 'locked' }
  | { status: 'choosing'; items: ExportItem[]; fresh: ExportItem[]; scope: ExportScope; lastExportedAt: string | null; unavailable: number; notice: string | null }
  | { status: 'preparing'; mode: ExportMode; controller: AbortController }
  | { status: 'running'; mode: ExportMode; controller: AbortController; progress: ExportProgress }
  | { status: 'done'; mode: ExportMode; result: ExportResult; zipFiles: number; context: ExportContext }
  | { status: 'error'; message: string };

type DirectoryPicker = (options?: { mode?: 'read' | 'readwrite' }) => Promise<FsDirHandle>;
const directoryPicker = (): DirectoryPicker | undefined =>
  typeof window === 'undefined' ? undefined : (window as unknown as { showDirectoryPicker?: DirectoryPicker }).showDirectoryPicker;

export function useAlbumExport(albumId: string, albumTitle: string, sources: ExportSource[], canExport = true) {
  const [state, setState] = useState<ExportState>({ status: 'idle' });
  const active = state.status === 'preparing' || state.status === 'running' ? state.controller : null;
  const busy = active !== null;

  // Leaving the page mid-export stops it. This also fires when a run ends, which is harmless.
  useEffect(() => (active ? () => active.abort() : undefined), [active]);

  useEffect(() => {
    if (!busy) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy]);

  const run = async (mode: ExportMode, items: ExportItem[], context: ExportContext) => {
    const controller = new AbortController();
    setState({ status: 'preparing', mode, controller });
    try {
      const measured = await measureItems(items, fetch, controller.signal);
      let sink = context.folderSink;
      let zipPartsUsed = context.zipPartsUsed;
      let zipFiles = 0; // ZIP files actually handed to the browser in this run
      if (!sink) {
        const parts = planParts(measured);
        const save = (blob: Blob, filename: string) => {
          saveBlob(blob, filename);
          zipFiles += 1;
        };
        sink = createZipSink({ title: albumTitle, nameSuffix: context.nameSuffix, parts, startPartNumber: zipPartsUsed + 1, save });
        zipPartsUsed += parts.length;
      }
      const result = await runExport(measured, sink, {
        signal: controller.signal,
        onProgress: progress => setState({ status: 'running', mode, controller, progress }),
      });
      recordExported(albumId, idsToRecord(mode, result));
      setState({ status: 'done', mode, result, zipFiles, context: { ...context, zipPartsUsed } });
    } catch (err) {
      setState({ status: 'error', message: err instanceof Error ? err.message : 'The export could not start.' });
    }
  };

  const open = () => {
    if (!canExport) {
      setState({ status: 'locked' });
      return;
    }
    const { items, unavailable } = buildExportItems(sources);
    const history = loadExportHistory(albumId);
    const { fresh } = splitByHistory(items, history);
    const scope: ExportScope = fresh.length > 0 && fresh.length < items.length ? 'new' : 'all';
    setState({ status: 'choosing', items, fresh, scope, lastExportedAt: history.lastExportedAt, unavailable, notice: null });
  };

  const close = () => {
    if (!busy) setState({ status: 'idle' });
  };

  const setScope = (scope: ExportScope) => {
    if (state.status === 'choosing') setState({ ...state, scope });
  };

  const exportToFolder = async () => {
    const picker = directoryPicker();
    if (state.status !== 'choosing' || !picker) return;
    let root: FsDirHandle;
    try {
      // Must be the first await: the picker needs the click's user activation.
      root = await picker({ mode: 'readwrite' });
    } catch (err) {
      if ((err as { name?: unknown } | null)?.name === 'AbortError') return; // picker dismissed
      setState({ ...state, notice: "Kizu couldn't open that folder. Choose a different one, or download a ZIP." });
      return;
    }
    let folderSink: ExportSink;
    try {
      folderSink = await createFolderSink(root, albumTitle, state.items);
    } catch {
      setState({ ...state, notice: "Kizu couldn't use that folder. Choose a different one, or download a ZIP." });
      return;
    }
    const chosen = state.scope === 'new' ? state.fresh : state.items;
    await run('folder', chosen, { folderSink, allItems: state.items, zipPartsUsed: 0, nameSuffix: '' });
  };

  const exportToZip = async () => {
    if (state.status !== 'choosing') return;
    const onlyNew = state.scope === 'new';
    const nameSuffix = onlyNew ? incrementalSuffix(new Date()) : '';
    await run('zip', onlyNew ? state.fresh : state.items, { folderSink: null, allItems: state.items, zipPartsUsed: 0, nameSuffix });
  };

  const cancel = () => active?.abort();

  const retryFailed = async () => {
    if (state.status !== 'done' || state.result.failed.length === 0) return;
    await run(state.mode, state.result.failed, state.context);
  };

  return { state, supportsFolder: typeof directoryPicker() === 'function', open, close, setScope, exportToFolder, exportToZip, cancel, retryFailed };
}

export type AlbumExport = ReturnType<typeof useAlbumExport>;
